import {
  Grid,
  GridItem,
  InputLeftElement,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import { FixAndFlipProduct } from "../../lib/models/enums";
import { selectDebtStatus } from "../../lib/models/short-term/short-term-form-selects.model";
import { FixAndFlipForm } from "../../lib/models/short-term/short-term-form.model";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import CheckboxField from "../form-fields/CheckboxField";
import SelectField from "../form-fields/SelectField";
import TextInputField from "../form-fields/TextInputField";

function DealTermsForm() {
  const [lesserOfLotOrPurchaseLabel, setLesserOfLotOrPurchaseLabel] =
    React.useState("Total As-Is Value");

  const { values, setFieldValue, initialValues, setFieldTouched } =
    useFormikContext<FixAndFlipForm>();
  const {
    loanProduct,
    loanPurpose,
    targetCloseDate,
    channel,
    deferredOrigPercent,
  } = values;
  const { loanPurpose: initialLoanPurpose } = initialValues;

  React.useEffect(() => {
    if (loanProduct === FixAndFlipProduct["BridgePlus"]) {
      setFieldValue("rehabConBudget", 0);
      setFieldValue("afterCompletedVal", values.lesserOfLotOrPurchase);
    }

    if (loanProduct === FixAndFlipProduct["New Construction"]) {
      setLesserOfLotOrPurchaseLabel("Lesser of Lot Value or Purchase Price");
    } else {
      setLesserOfLotOrPurchaseLabel("Total As-Is Value");
    }
  }, [
    loanProduct,
    setFieldValue,
    values.lesserOfLotOrPurchase,
    values.afterCompletedVal,
  ]);

  React.useEffect(() => {
    if (initialLoanPurpose === loanPurpose) {
      if (initialLoanPurpose === "Purchase") {
        setFieldValue("existingDebtStatus", "Purchase");
        setFieldValue("originalPurchaseDate", targetCloseDate);
      }
    } else {
      if (loanPurpose === "Purchase") {
        setFieldValue("existingDebtStatus", "Purchase");
        setFieldValue("originalPurchaseDate", targetCloseDate);
      } else {
        setFieldValue("existingDebtStatus", "");
        setFieldTouched("existingDebtStatus", true, true);
      }
    }
  }, [
    initialLoanPurpose,
    loanPurpose,
    targetCloseDate,
    setFieldValue,
    setFieldTouched,
  ]);

  React.useEffect(() => {
    if (!!Number(deferredOrigPercent)) {
      setFieldValue("buyUpPercentage", 0);
      setFieldValue("isZeroOrigFee", "No");
    }
  }, [deferredOrigPercent, setFieldValue]);

  return (
    <Grid
      templateColumns="repeat(12, 1fr)"
      gap={{ base: 6, md: 8, lg: 8 }}
      paddingBottom={8}
    >
      <GridItem colSpan={12}>
        <FormSectionHeader id="deal-terms" label="Deal Terms" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Original Purchase Date"
          name="originalPurchaseDate"
          type="date"
          disabled={loanPurpose === "Purchase"}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Target Close Date"
          name="targetCloseDate"
          type="date"
        />
      </GridItem>

      {loanPurpose === "Purchase" && (
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <TextInputField
            label="Total Purchase Price"
            name="totalPurchasePrice"
            type="number"
            formatNumber
            children={<InputLeftElement children="$" />}
          />
        </GridItem>
      )}

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label={lesserOfLotOrPurchaseLabel}
          name="lesserOfLotOrPurchase"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          disabled={loanProduct === FixAndFlipProduct["BridgePlus"]}
          label="Rehab/Construction Budget"
          name="rehabConBudget"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Renovation Costs After Purchase"
          name="renovationCostsAfterPurchase"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          disabled={loanProduct === FixAndFlipProduct["BridgePlus"]}
          label="Value After Completion"
          name="afterCompletedVal"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SelectField
          label="Existing Debt Status"
          name="existingDebtStatus"
          data={selectDebtStatus}
          disabled={loanPurpose === "Purchase"}
          placeholder="Select an existing debt status"
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Existing Debt Amount (Payoff)"
          name="payoff"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Number of Properties"
          name="numOfProperties"
          type="number"
          formatNumber
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <CheckboxField label="High Risk Property?" name="highRisk" />
      </GridItem>

      {channel.includes("Broker") && (
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <CheckboxField
            label="Zero Percent Lender Fee?"
            name="isZeroOrigFee"
            disabled={!!Number(deferredOrigPercent)}
          />
        </GridItem>
      )}
    </Grid>
  );
}

export default DealTermsForm;
